/**
 * @file receipt-commit-block.ts
 * @description Что НЕ даёт провести приёмку (решение пользователя 2026-09-26,
 * W17-9b): «Внутренний выпуск» проводится только с привязанным и проведённым
 * списанием сырья «В производство». Это блок, а не предупреждение — в
 * отличие от срока годности (W20 «мягко»).
 *
 * Источников два, и оба нужны:
 *   - свой расчёт по документу (`localCommitBlocks`) — кнопка гаснет сразу,
 *     без запроса, пока кладовщик выбирает причину;
 *   - ответ `commit-check` (`splitCommitCheck`) — сервер знает больше и может
 *     прислать блок отдельным списком (`errors[]`) или пометкой `blocking`.
 * Коды блоков — реестром: новый блокирующий код = строка в `BLOCKING_CODES`.
 */

import type { GoodsReceiptDto, ReceiptWarning } from "../../lib/api-wms";

/** Блок проведения: код сервера и текст для полосы. */
export interface ReceiptCommitBlock {
  code: string;
  message: string;
}

/** Тексты блоков, если сервер своего не прислал. */
export const COMMIT_BLOCK_TEXT: Record<string, string> = {
  parent_writeoff_required:
    "Внутренний выпуск проводится только с привязанным списанием сырья «В производство» — укажите парное списание.",
  parent_writeoff_not_committed:
    "Парное списание «В производство» ещё черновик — проведите списание сырья, затем приёмку.",
};

/** Коды, которые проведение блокируют (старый `parent_writeoff_missing` — тоже). */
const BLOCKING_CODES: Record<string, string> = {
  parent_writeoff_required: "parent_writeoff_required",
  parent_writeoff_not_committed: "parent_writeoff_not_committed",
  parent_writeoff_missing: "parent_writeoff_required",
};

function toBlock(item: ReceiptWarning): ReceiptCommitBlock {
  const code = BLOCKING_CODES[item.code] ?? item.code;
  return { code, message: item.message || COMMIT_BLOCK_TEXT[code] || "Приёмку нельзя провести" };
}

/** Ответ `commit-check` → блоки и мягкие предупреждения. */
export function splitCommitCheck(res: {
  warnings?: ReceiptWarning[];
  errors?: ReceiptWarning[];
}): { blocking: ReceiptCommitBlock[]; warnings: ReceiptWarning[] } {
  const blocking = (res.errors ?? []).map(toBlock);
  const warnings: ReceiptWarning[] = [];
  for (const item of res.warnings ?? []) {
    if (item.blocking || item.code in BLOCKING_CODES) blocking.push(toBlock(item));
    else warnings.push(item);
  }
  return { blocking: dedupe(blocking), warnings };
}

/** Блоки, видимые по самому документу-черновику (без запроса). */
export function localCommitBlocks(receipt: GoodsReceiptDto | null): ReceiptCommitBlock[] {
  if (!receipt || receipt.status !== "DRAFT" || receipt.reason !== "INTERNAL_OUTPUT") return [];
  if (!receipt.parentWriteoffId) {
    return [{ code: "parent_writeoff_required", message: COMMIT_BLOCK_TEXT.parent_writeoff_required! }];
  }
  if (receipt.parentWriteoff && receipt.parentWriteoff.status !== "COMMITTED") {
    return [{ code: "parent_writeoff_not_committed", message: COMMIT_BLOCK_TEXT.parent_writeoff_not_committed! }];
  }
  return [];
}

/** id блока «Причина приёмки» — к нему ведёт «Указать парное списание». */
export const RECEIPT_REASON_FIELDS_ID = "receipt-reason-fields";

/** Прокрутить к причине приёмки и поставить фокус на выбор парного списания. */
export function focusReceiptReasonFields(): void {
  const block = document.getElementById(RECEIPT_REASON_FIELDS_ID);
  if (!block) return;
  if (typeof block.scrollIntoView === "function") block.scrollIntoView({ block: "center", behavior: "smooth" });
  const target =
    block.querySelector<HTMLElement>('[aria-label="Парное списание"]') ??
    block.querySelector<HTMLElement>('[aria-label="Причина приёмки"]');
  target?.focus();
}

/** Один код — одна полоса. */
export function dedupe(blocks: ReceiptCommitBlock[]): ReceiptCommitBlock[] {
  const seen = new Set<string>();
  return blocks.filter((b) => (seen.has(b.code) ? false : (seen.add(b.code), true)));
}
